/**
 * Derived caches the log keeps alongside the event buffer: unit metadata (so meters and the
 * exporter still know units that were despawned) and pull segments.
 */
import type { EncounterId, Faction, Ms, NpcId, NpcTier, PackId, Role, ClassId, Unit, UnitId, UnitKind, World } from '../contracts';

export interface UnitMeta {
  id: UnitId;
  name: string;
  kind: UnitKind;
  faction: Faction;
  role: Role | null;
  classId: ClassId | null;
  npcId: NpcId | null;
  tier: NpcTier | null;
  packId: PackId | null;
  encounterId: EncounterId | null;
  summonerId: UnitId | null;
}

export interface SegmentRecord {
  pull: number;
  startMs: Ms;
  endMs: Ms | null;
  packIds: PackId[];
  /** Enemy unit ids that belonged to the pull's packs (filled at pull_end). */
  memberIds: UnitId[];
  killedIds: UnitId[];
  encounterId: EncounterId | null;
  isBoss: boolean;
  wipe: boolean;
}

export function metaOf(u: Unit): UnitMeta {
  return {
    id: u.id,
    name: u.name,
    kind: u.kind,
    faction: u.faction,
    role: u.role,
    classId: u.classId,
    npcId: u.npcId,
    tier: u.tier,
    packId: u.packId,
    encounterId: u.encounterId,
    summonerId: u.summonerId,
  };
}

/** Pack of a unit (summons inherit their summoner's pack). */
export function packOfMeta(meta: ReadonlyMap<UnitId, UnitMeta>, m: UnitMeta): PackId | null {
  if (m.packId) return m.packId;
  return m.summonerId ? (meta.get(m.summonerId)?.packId ?? null) : null;
}

/** Owner for attribution: a summon owned by a party member counts as that member (WCL pets). */
export function ownerOf(meta: ReadonlyMap<UnitId, UnitMeta>, id: UnitId): UnitId {
  const m = meta.get(id);
  if (!m?.summonerId) return id;
  const owner = meta.get(m.summonerId);
  return owner && owner.faction === m.faction && owner.kind === 'player' ? owner.id : id;
}

/** Enemy unit ids in the world that belong to any of the given packs. */
export function membersOfPacks(world: World, packIds: readonly PackId[]): UnitId[] {
  const out: UnitId[] = [];
  for (const id of world.unitOrder) {
    const u = world.units[id];
    if (!u || u.faction !== 'enemy') continue;
    const pack = u.packId ?? (u.summonerId ? (world.units[u.summonerId]?.packId ?? null) : null);
    if (pack && packIds.includes(pack)) out.push(id);
  }
  return out;
}
